import React, { useEffect } from 'react';
import ImpressionStore from '../../stores/impression';
import HierarchicalReportLayout, { formatters } from 'components/hierarchical-report/Layout';

import { Link } from 'react-router-dom';
import queryString from 'query-string';
import { Button } from 'twyne-new.ui';
import { ImpressionReportProvider, useImpressionReportStore } from './store'
import { debounce } from 'lodash';
import Moment from 'moment';
import { ChartColumnIncreasing } from 'lucide-react';
import appStore from '@Stores/app';

export default class ImpressionLayout extends HierarchicalReportLayout {
  store = ImpressionStore
  title = 'Impression'

  useNewStore = () => {
    const store = useImpressionReportStore()
    const { report: { useLoad } } = store
    useLoad()
    return store
  }

  configure = {
    filterby: {
      options: [{
        value: 'path',
        get label() {
          return <formatters.Loadable isLoading={ImpressionStore.path.isLoading}>Path</formatters.Loadable>
        },
        options: ImpressionStore.path.fetchData
      }, {
        value: 'page',
        get label() {
          return <formatters.Loadable isLoading={ImpressionStore.page.isLoading}>Page</formatters.Loadable>
        },
        options: ImpressionStore.page.fetchData
      }, {
        value: 'publisher',
        get label() {
          return <formatters.Loadable isLoading={ImpressionStore.publisher.isLoading}>Publisher</formatters.Loadable>
        },
        options: ImpressionStore.publisher.fetchData
      }, {
        value: 'allocation',
        get label() {
          return <formatters.Loadable isLoading={ImpressionStore.allocation.isLoading}>Allocation</formatters.Loadable>
        },
        options: ImpressionStore.allocation.fetchData
      }, {
        value: 'pathUserProfile',
        get label() {
          return <formatters.Loadable isLoading={ImpressionStore.pathUserProfile.isLoading}>Path User Profile</formatters.Loadable>
        },
        options: ImpressionStore.pathUserProfile.fetchData
      }, {
        value: 'offer',
        get label() {
          return <formatters.Loadable isLoading={ImpressionStore.offer.isLoading}>Offer</formatters.Loadable>
        },
        options: ImpressionStore.offer.fetchData
      }, {
        value: 'domain',
        get label() {
          return <formatters.Loadable>Domain</formatters.Loadable>
        },
        options: async () => appStore.site.domain_aliases
          .split(",")
          .filter(value => !value?.includes("*"))
          .map(value => {
            value = JSON.parse(value)
            return { label: value, value, getLabel: () => value, getValue: () => value }
          }),
      }],
    },
    groupby: {
      default: ['path', 'page'],
      options: [
        { value: 'publisher', label: 'Publisher', group: 'Traffic', named: true },
        { value: 'allocation', label: 'Allocation', group: 'Traffic', named: true },
        { value: 'sid1', label: 'SubID 1', group: 'Traffic' },
        { value: 'sid2', label: 'SubID 2', group: 'Traffic' },
        { value: 'device', label: 'Device', group: 'Traffic' },
        { value: 'optout', label: 'Optout', group: 'Traffic' },
        { value: 'domain', label: 'Domain', group: 'Traffic' },

        { value: 'offer', label: 'Offer', group: 'User Session' },
        { value: 'hour_session', label: 'Creation Hour', group: 'User Session' },
        { value: 'day_session', label: 'Creation Day', group: 'User Session' },
        { value: 'month_session', label: 'Creation Month', group: 'User Session' },
        { value: 'registrationCompleted', label: 'Registration Completed', group: 'User Session' },
        { value: 'duplicated', label: 'Duplicated', group: 'User Session' },

        { value: 'pathUserProfile', label: 'Path User Profile', group: 'Midpath' },
        { value: 'path', label: 'Path', group: 'Midpath' },
        { value: 'slot', label: 'Slot', group: 'Midpath' },
        { value: 'page', label: 'Page', group: 'Midpath' },
        { value: 'pageVersion', label: 'Page Version', group: 'Midpath' },
        { value: 'partner', label: 'Partner', group: 'Midpath' },
        { value: 'completedOrigin', label: 'Completed Origin', group: 'Midpath' },
        { value: 'pathContainerSlot', label: 'Path Container Slot', group: 'Midpath' },
        { value: 'experimentVersion', label: 'Experiment Version', group: 'Midpath' },

        { value: 'hour', label: 'Hour', group: 'Full Time' },
        { value: 'day', label: 'Day', group: 'Full Time' },
        { value: 'month', label: 'Month', group: 'Full Time' },
        { value: 'year', label: 'Year', group: 'Full Time' },

        // { value: 'hour_unittime', label: 'Hour', group: 'Unit Time' },
        // { value: 'day_unittime', label: 'Day', group: 'Unit Time' },
        // { value: 'weekday_unittime', label: 'Weekday', group: 'Unit Time' },
        // { value: 'month_unittime', label: 'Month', group: 'Unit Time' },

        { value: 'city', label: 'City', group: 'Consumer' },
        { value: 'state', label: 'State', group: 'Consumer' },
        { value: 'emailDomain', label: 'Email Domain', group: 'Consumer' },
        { value: 'age', label: 'Age', group: 'Consumer' },
        { value: 'gender', label: 'Gender', group: 'Consumer' },
      ]
    },
    metrics: {
      default: ['totalPC', 'completed', 'notCompleted', 'uniqUser', 'uniqUserSession', 'revenue', 'rps', 'rpus', 'rpu'],
      options: [
        { value: 'uniqUserSession', label: 'Users Session', group: 'Default' },

        { value: 'completed', label: 'Page Completed', group: 'Action' },
        { value: 'notCompleted', label: 'Page Not Completed', group: 'Action' },

        { value: 'completedPC', label: 'Page Completed %', group: 'Ratio' },
        { value: 'notCompletedPC', label: 'Page Not Completed %', group: 'Ratio' },
        { value: 'uniqPathSessionPC', label: 'Path Session Impressions %', group: 'Ratio' },

        { value: 'revenue', label: 'Revenue', group: 'Monetization' },
        { value: 'rpi', label: <span>RPI $ (<small><i>Rev. / Impression</i></small>)</span>, group: 'Monetization' },
        { value: 'rps', label: <span>RPS $ (<small><i>Rev. / Path Session</i></small>)</span>, group: 'Monetization' },
        { value: 'rpu', label: <span>RPu $ (<small><i>Rev. / Uniq. Users</i></small>)</span>, group: 'Monetization' },
        { value: 'rpus', label: <span>RPUS $ (<small><i>Rev. / Uniq. User Sessions</i></small>)</span>, group: 'Monetization' },
      ]
    },
    sortby: {
      default: ['total desc'],
      options: [
        { value: 'total', label: 'Impressions', group: 'Default' },
      ]
    }
  }

  metricColumns = [{
    dataField: 'uniqUserSession',
    customSort: true,
    text: 'User Sessions',
    formatter: formatters.parentPercent(formatters.number, 'uniqUserSession')
  }, {
    dataField: 'uniqPathSessionPC',
    customSort: true,
    text: "Path Session Impressions (%)",
    formatter: cell => formatters.percent(cell)
  }, {
    dataField: 'completed',
    customSort: true,
    text: "Page Completed",
    formatter: formatters.drilldown(this.newTab, 'completed')
  }, {
    dataField: 'completedPC',
    customSort: true,
    text: "Page Completed (%)",
    formatter: cell => formatters.percent(cell)
  }, {
    dataField: 'notCompleted',
    customSort: true,
    text: "Page Not Completed",
    formatter: formatters.drilldown(this.newTab, 'notCompleted')
  }, {
    dataField: 'notCompletedPC',
    customSort: true,
    text: "Page Not Completed (%)",
    formatter: cell => formatters.percent(cell)
  }, {
    dataField: 'rpi',
    customSort: true,
    text: "RPI",
    formatter: formatters.parentPercent(formatters.money(), 'rpi')
  }, {
    dataField: 'rps',
    customSort: true,
    text: "RPS",
    formatter: formatters.parentPercent(formatters.money(), 'rps')
  }, {
    dataField: 'rpus',
    customSort: true,
    text: "RPUS",
    formatter: formatters.parentPercent(formatters.money(), 'rpus')
  }, {
    dataField: 'rpu',
    customSort: true,
    text: "RPU",
    formatter: formatters.parentPercent(formatters.money(), 'rpu')
  }, {
    dataField: 'revenue',
    customSort: true,
    text: "Revenue",
    formatter: formatters.parentPercent(formatters.money(), 'revenue')
  }]

  get columns() {
    const { metrics, groupby } = this.state || {}
    const metricColumns = this.metricColumns.filter(d => metrics && metrics.includes(d.dataField))

    return [{
      dataField: 'value',
      text: groupby && groupby.join(' / '),
      size: 300,
      customSort: true,
      sortValue: row => row.name || row.keyString || row.value,
      formatter: formatters.name
    }, {
      dataField: 'uniqPathSession',
      customSort: true,
      text: 'Path Session Impressions',
      formatter: formatters.parentPercent(formatters.number, 'uniqPathSession')
    }, {
      dataField: 'uniqUser',
      customSort: true,
      text: 'Unique Users',
      formatter: formatters.parentPercent(formatters.number, 'uniqUser')
    }, {
      dataField: 'total',
      customSort: true,
      text: 'Page Impressions',
      formatter: formatters.drilldown(this.newTab, 'total'),
    },
    ...metricColumns,
    {
      dataField: 'progressLink',
      customSort: false,
      text: "",
      formatter: (_, row) => {
        const siteId = row.key && row.key == 'site' ? row.value : null
        if (siteId) {
          let { startDate, endDate } = this.state;
          startDate = startDate ? startDate.format('MM-DD-YYYY') : null;
          endDate = endDate ? endDate.format('MM-DD-YYYY') : null;
          const query = queryString.stringify({ siteId, startDate, endDate })

          return (
            <Link to={{
              pathname: '/reports/progression',
              search: query
            }}>
              <Button size="sm" color="accent" variant='ghost'>
                <ChartColumnIncreasing size={16} /> 
                Progression
              </Button>
            </Link>
          );
        }
      },
    }]
  }

  filterbyQuery() {
    const query = this.props.location.search && queryString.parse(this.props.location.search) || {};

    if (JSON.stringify(query) === "{}" || this.props.location.search.includes('configure')) return;

    if (query.startDate) {
      query.startDate = Moment(query.startDate, 'MM-DD-YYYY');
    }

    if (query.endDate) {
      query.endDate = Moment(query.endDate, 'MM-DD-YYYY');
    }

    if (query.filterby) {
      query.filterby = JSON.parse(query.filterby);
    }

    if (!Array.isArray(query.sortby)) query.sortby = [query.sortby]

    if (!Array.isArray(query.groupby)) query.groupby = [query.groupby]

    debounce(() => {
      this.setState({ startDate: query.startDate, endDate: query.endDate, groupby: query.groupby, metrics: query.metrics, sortby: query.sortby, filterby: query.filterby }, this.loadDataState);
    }, 300)()
  }

  ReportProvider = ({ children }) => {
    useEffect(() => {
      this.filterbyQuery()
    }, [])

    return <ImpressionReportProvider>
      {children}
    </ImpressionReportProvider>
  }
}
